import React, {useEffect, useState} from "react";
import PropTypes from "prop-types";

import CustomDropdown, {DROPDOWN_OPEN_EVENT_NAME} from "../CustomDropdown";
import LinearLoader from "../LinearLoader";

import '../Autocomplete/autocomplete.scss';
import './autocomplete_multiple.scss';

const AutocompleteMultiple = function (
    {
        id,
        fetchItems,
        onSelect,
        renderItem,
        renderAdd,
        onReset,
        label,
        default_value,
        placeholder = '',
        container_class = '',
        input_class = '',
        label_class = '',
        persist_value = false,
        show_request_in_dropdown = false,
        always_show_add = false,
        with_reset = false,
        disabled = false,
        selected_items = [],
        match_dropdown_width = false
    }
) {
    const [input, setInput] = useState(default_value ? default_value : '');
    const [items, setItems] = useState([]);
    const [requesting, setRequesting] = useState(false);
    const [isRequested, setIsRequested] = useState(false);

    function onInputChange(event) {
        event.preventDefault();
        const input_new = event.target.value;
        setInput(input_new);
        setIsRequested(false);
        setItems([]);
        if (id) {
            window.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT_NAME + id));
        }
        if (input_new.length >= 3) {
            action();
        }
    }

    function action() {
        if (input.length >= 3) {
            setIsRequested(false);
            setRequesting(true);
            fetchItems(input)
                .then(items => {
                    setIsRequested(true);
                    setRequesting(false);
                    setItems(items);
                })
                .catch(error => {
                    console.error(error);
                    setIsRequested(false);
                    setRequesting(false);
                });
        }
    }

    function reset() {
        setInput('');
        setItems([]);
        setIsRequested(false);
        setRequesting(false);
        onReset();
    }

    function renderInputLabel() {
        if (label) {
            return (
                <label className={label_class}>{label}</label>
            );
        }

        return null;
    }

    function renderTrigger() {
        return (
            <div className="autocomplete-input">
                {renderInputLabel()}
                <input
                    type="text"
                    autoComplete="off"
                    className={"autocomplete-input-field " + input_class}
                    id="autocomplete-input-field"
                    value={input}
                    onChange={onInputChange}
                    placeholder={placeholder}
                    disabled={disabled}
                />
            </div>
        );
    }

    function renderReset() {
        if (with_reset) {
            if (typeof onReset !== 'function') {
                throw 'You must define "onReset" property in AutocompleteMultiple component when setting "with_reset" property to "true"';
            }

            return (
                <i
                    className='autocomplete-reset fas fa-times-circle'
                    onClick={() => reset()}
                />
            );
        }

        return null;
    }

    function renderContent() {
        let objects = {};
        if (input.length < 3) {
            objects['more-letters'] = (
                <div className="autocomplete-dropdown-menu-head">Please enter at least 3 letters</div>);
        } else if (isRequested && !requesting && items.length <= 0) {
            if (show_request_in_dropdown) {
                const item = {
                    email: input//TODO WHY email????
                };
                objects['request-item'] = (
                    <div onClick={() => onSelect(item)}>
                        {renderItem(item)}
                    </div>
                );
            } else {
                objects['no-items'] = (<div className="autocomplete-dropdown-menu-head">Nothing found</div>);
            }
        } else if (isRequested && !requesting && items.length > 0) {
        } else if (requesting) {
            objects['not-found'] = (
                <div className="autocomplete-dropdown-menu-head">
                    <div>Searching ...</div>
                    <LinearLoader/>
                </div>
            );
        } else {
            objects['continue'] = (<div className="autocomplete-dropdown-menu-head">Finish typing</div>);
        }
        items.map((item, index) => {
            objects[index] = (
                <div onClick={() => onSelect(item)}>
                    {renderItem(item)}
                </div>
            );
        });

        if (typeof renderAdd === 'function' && isRequested && !requesting && (items.length <= 0 || always_show_add)) {
            objects['add'] = renderAdd(input_new => {
                setInput(input_new);
                setIsRequested(persist_value);
                if (!persist_value) {
                    setItems([]);
                }
            });
        }

        return objects;
    }

    useEffect(() => {
        if (id && with_reset) {
            window.addEventListener('mindsets-autocomplete-reset-' + id, () => reset());

            return () => {
                window.removeEventListener('mindsets-autocomplete-reset-' + id, () => reset());
            }
        }
    }, []);

    useEffect(() => {
        if (!id) {
            id = Date.now() + '-' + Math.floor(Math.random() * 100);
        }
    }, []);

    return (
        <div className={"autocomplete " + container_class}>
            <CustomDropdown
                id={id}
                trigger={renderTrigger()}
                no_wrap={true}
                content_class='autocomplete-dropdown-menu'
                item_class='autocomplete-dropdown-menu-item'
                items={renderContent()}
                disabled={disabled}
                match_dropdown_width={match_dropdown_width}
                use_arrow={false}
            />
            {renderReset()}
            <div className='d-flex flex-wrap autocomplete-selected-items'>{selected_items.map((selected_item, index) => {
                return (
                    <div key={index} className='autocomplete-selected-item'>{renderItem(selected_item)}</div>
                );
            })}</div>
        </div>
    );
}

AutocompleteMultiple.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    container_class: PropTypes.string,
    input_class: PropTypes.string,
    label_class: PropTypes.string,
    selected_items: PropTypes.array,
    fetchItems: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    renderItem: PropTypes.func.isRequired,
    renderAdd: PropTypes.func,
    onReset: PropTypes.func,
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
        PropTypes.element,
    ]),
    default_value: PropTypes.string,//If provided, the input field will be preset with it
    persist_value: PropTypes.bool,//If TRUE and default_value is provided, the input field will keep its value as well as the search results in dropdown
    show_request_in_dropdown: PropTypes.bool,
    always_show_add: PropTypes.bool,
    with_reset: PropTypes.bool,
    disabled: PropTypes.bool,
    match_dropdown_width: PropTypes.bool
}

export default AutocompleteMultiple;
